import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { apiFetch } from '../../../../api/crud';
import { REWARD_ID_PARAM, REWARD_ID_ROUTE_PARAM, ROUTE_REWARD_ID } from '../../config/routes';
import { SHIPMENTS_TRACKERS_PATH } from '../../config/service';
import { POSTHOG_CAPTURE_ATTRIBUTES } from '../../config/tracker';
import Dashboard from '../../global/Dashboard';
import DealsPreview from '../Deals/DealsPreview';
import DealsPreviewBanner from '../Deals/DealsPreview/DealsPreviewBanner';
import { getCategories } from '../../utils/service';
import useDealsSegmentation, { TBB_SEGMENTS_TYPES } from '../../../../hooks/useDealsSegmentation';
import { isMobile, SIZE_SM, SIZE_XS } from '../../../../hooks/useBreakPoint';
import MarketingSlider from '../../global/MarketingSlider';

import './DashboardDeals.scss';
import { REWARD_PREVIEW_TYPES } from '../../config/rewards';
import OffersPreviewV2 from '../OffersV2/OffersPreviewV2';

const DashboardDeals = () => {
  const history = useHistory();
  const isMobileXsSm = isMobile([SIZE_XS, SIZE_SM]);

  // Debug: confirms this component (dashboard home) is rendering with latest code
  console.log('DashboardDeals mounted (shipment trackers request runs from this page)');

  const {
    isLoading: isLoadingSegmentation,
    isNonTbbUser,
    dashboardCategories,
  } = useDealsSegmentation();

  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [categories, setCategories] = useState([]);

  const isLoading = isLoadingSegmentation || isLoadingCategories;

  const loadCategories = useCallback(async () => {
    setIsLoadingCategories(true);

    const { data: categoriesData } = await getCategories({});

    setCategories(categoriesData.categories);
    setIsLoadingCategories(false);
  }, []);

  const getDealsContent = useCallback(() => {
    if (isLoading) {
      return null;
    }

    if (isNonTbbUser) {
      const trackerProps = {
        [`${POSTHOG_CAPTURE_ATTRIBUTES.SEGMENT}-${TBB_SEGMENTS_TYPES.NON_TBB_USERS}`]: isNonTbbUser,
      };

      return (
        <>
          <DealsPreview
            title="Starter Deals"
            subTitle="Save instantly, earn bonus TrashieCash™"
            type={REWARD_PREVIEW_TYPES.STARTER}
            showOnEmptyResults
            trackerProps={trackerProps}
          />
          <DealsPreviewBanner
            trackerProps={trackerProps}
          />
        </>
      );
    }

    return (
      <>
        <DealsPreview
          title="Featured Deals"
          type={REWARD_PREVIEW_TYPES.FEATURED}
          alwaysShowCta
        />
        <DealsPreview
          title="For You"
          type={REWARD_PREVIEW_TYPES.EXCLUSIVE}
          showOnEmptyResults
        />
        <MarketingSlider isDeals />
        <DealsPreview
          title="Hot Deals"
          type={REWARD_PREVIEW_TYPES.HOT}
          showOnEmptyResults
        />
        {dashboardCategories.map(categoryId => {
          const category = categories.find(({ uid }) => uid === categoryId);

          if (!category) {
            return null;
          }

          return (
            <DealsPreview
              key={category.uid}
              title={category.name}
              type={REWARD_PREVIEW_TYPES.CATEGORY}
              category={category}
            />
          );
        })}
      </>
    );
  }, [
    isLoading,
    isNonTbbUser,
    dashboardCategories,
    categories,
    isMobileXsSm]);

  useEffect(() => {
    // Start support for old rewards url from webflow
    const searchParams = new URLSearchParams(history.location.search);
    const searchParamRewardId = searchParams.get(REWARD_ID_PARAM);

    if (searchParamRewardId) {
      history.replace(ROUTE_REWARD_ID.replace(REWARD_ID_ROUTE_PARAM, searchParamRewardId));
    }
    // End support for old rewards url from webflow

    loadCategories();
  }, []);

  useEffect(() => {
    console.log('\n\n\nShipment trackers: fetching...\n\n\n\n');
    apiFetch({ path: SHIPMENTS_TRACKERS_PATH })
      .then((result) => {
        console.log('Shipment trackers (QA):', result);
      })
      .catch((err) => {
        console.log('Shipment trackers request failed:', err);
      });
  }, []);

  return (
    <Dashboard>
      {getDealsContent()}
      <OffersPreviewV2 />
    </Dashboard>
  );
};

export default DashboardDeals;
